import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiResponse } from "@typeDefinitions/Api";
import { GetCurrentSubscriptionResponse } from "@client/clinic/getCurrentSubscriptionAPI";
import { dietitianKeys } from "@hooks/queries/dietitian/dietitianKeys";
import { GetDietitianAccountResponse } from "@client/settings/getDietitianAccountAPI";
import { settingsKeys } from "@hooks/queries/settings/settingsKeys";
import { SubscriptionTypes } from "@utils/user";
import { SubscriptionPlanCode } from "@client/resources/CurrentSubscriptionResource";

const mapSubscriptionTypeToPlanCode = (
  subscriptionTypeId?: number | null,
): SubscriptionPlanCode | null => {
  switch (subscriptionTypeId) {
    case SubscriptionTypes.TRIAL:
      return SubscriptionPlanCode.TRIAL;
    case SubscriptionTypes.UNLIMITED_GROWTH:
      return SubscriptionPlanCode.UNLIMITED_GROWTH;
    case SubscriptionTypes.GROWTH:
    case SubscriptionTypes.GROWTH_10:
    case SubscriptionTypes.GROWTH_30:
    case SubscriptionTypes.GROWTH_60:
      return SubscriptionPlanCode.GROWTH;
    case SubscriptionTypes.STARTER:
      return SubscriptionPlanCode.STARTER;
    case SubscriptionTypes.ENTERPRISE:
      return SubscriptionPlanCode.ENTERPRISE;
    case SubscriptionTypes.STUDENT:
      return SubscriptionPlanCode.COLLEGE;
    default:
      return null;
  }
};

const getSubscriptionTypeFromCurrentSubscription = (
  data: GetCurrentSubscriptionResponse | undefined,
): SubscriptionPlanCode | null => {
  const code = data?.data?.plan?.code;
  if (!code) {
    return null;
  }
  return Object.values(SubscriptionPlanCode).includes(
    code as SubscriptionPlanCode,
  )
    ? (code as SubscriptionPlanCode)
    : null;
};

const getSubscriptionTypeFromAccount = (
  data: ApiResponse<GetDietitianAccountResponse> | undefined,
): SubscriptionPlanCode | null => {
  const account = data?.data;
  const subscriptionTypeId = account?.clinic?.subscription?.type?.id ?? null;

  return mapSubscriptionTypeToPlanCode(subscriptionTypeId);
};

const useClinicSubscriptionTypeForUserDotCom = () => {
  const queryClient = useQueryClient();

  const currentSubscriptionQueryKey = dietitianKeys.currentSubscription();
  const accountQueryKey = settingsKeys.dietitianAccount();

  const [subscriptionType, setSubscriptionType] =
    useState<SubscriptionPlanCode | null>(() => {
      const accountType = getSubscriptionTypeFromAccount(
        queryClient.getQueryData<ApiResponse<GetDietitianAccountResponse>>(
          accountQueryKey,
        ),
      );

      if (accountType !== null) {
        return accountType;
      }

      return getSubscriptionTypeFromCurrentSubscription(
        queryClient.getQueryData<GetCurrentSubscriptionResponse>(
          currentSubscriptionQueryKey,
        ),
      );
    });

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(event => {
      if (event?.query?.queryKey && event.type === "updated") {
        const key = event.query.queryKey;

        if (
          JSON.stringify(key) === JSON.stringify(currentSubscriptionQueryKey) ||
          JSON.stringify(key) === JSON.stringify(accountQueryKey)
        ) {
          const accountType = getSubscriptionTypeFromAccount(
            queryClient.getQueryData<ApiResponse<GetDietitianAccountResponse>>(
              accountQueryKey,
            ),
          );

          if (accountType !== null) {
            setSubscriptionType(accountType);
            return;
          }

          setSubscriptionType(
            getSubscriptionTypeFromCurrentSubscription(
              queryClient.getQueryData<GetCurrentSubscriptionResponse>(
                currentSubscriptionQueryKey,
              ),
            ),
          );
        }
      }
    });

    return () => unsubscribe();
  }, [queryClient, currentSubscriptionQueryKey, accountQueryKey]);

  return { subscriptionType };
};

export default useClinicSubscriptionTypeForUserDotCom;
